import { createEffect, createSignal, For } from 'solid-js';
import type { JSX } from '@solidjs/web';
import { Meta, Title } from '@solidjs/meta';
import Sprite from './Sprite';
import { onMount } from '~/lib/mount';

const NAV = [
  ['/', 'Accueil', false],
  ['/#services', 'Nos services', true],
  ['/#pourquoi', 'Pourquoi TG3R', true],
  ['/contact', 'Contact', false],
] as const;

const SERVICES = [
  'Distribution & transport',
  'Réseautage commercial',
  'Mise en inventaire & référencement',
  "Gestion d'inventaire",
  'Développement commercial',
];

function Logo(props: { lazy?: boolean }) {
  return (
    <img
      class="brand-logo"
      src="/logo-tg3r.png"
      width="283"
      height="192"
      alt="TG3R — ça livre."
      loading={props.lazy ? 'lazy' : 'eager'}
      decoding="async"
    />
  );
}

/** En-tête : fond opaque et ombre dès que la page a défilé de quelques pixels. */
function useScrolled() {
  const [scrolled, setScrolled] = createSignal(false);
  onMount(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    addEventListener('scroll', onScroll, { passive: true });
    return () => removeEventListener('scroll', onScroll);
  });
  return scrolled;
}

/** Tiroir mobile : verrou du défilement, `inert` sur le reste, Échap, clic hors
 *  de l'en-tête et repli automatique quand la fenêtre repasse en mode large. */
function useDrawer(headerEl: () => HTMLElement | undefined, toggleEl: () => HTMLElement | undefined) {
  const [open, setOpen] = createSignal(false);
  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) toggleEl()?.focus();
  };
  createEffect(open, (isOpen) => {
    document.body.classList.toggle('is-locked', isOpen);
    for (const el of document.querySelectorAll('main, .site-footer')) {
      if (isOpen) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
    }
  });
  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open()) {
        e.preventDefault();
        close(true);
      }
    };
    const onPointer = (e: PointerEvent) => {
      const h = headerEl();
      if (open() && h && !h.contains(e.target as Node)) close();
    };
    const mq = matchMedia('(max-width: 900px)');
    const onMq = (m: MediaQueryListEvent) => {
      if (!m.matches && open()) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    mq.addEventListener('change', onMq);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
      mq.removeEventListener('change', onMq);
    };
  });
  return { open, toggle: () => setOpen(!open()), close };
}

export default function Root(props: { children: JSX.Element }) {
  let headerEl: HTMLElement | undefined;
  let toggleEl: HTMLButtonElement | undefined;
  const scrolled = useScrolled();
  const drawer = useDrawer(
    () => headerEl,
    () => toggleEl,
  );
  return (
    <>
      <Title>TG3R — Distribution et logistique alimentaire au Québec</Title>
      <Meta name="theme-color" content="#0A1628" />
      <a class="skip-link" href="#main">
        Aller au contenu principal
      </a>
      <Sprite />

      <header class={scrolled() ? 'site-header is-scrolled' : 'site-header'} ref={headerEl}>
        <div class="container">
          <nav class="nav" id="nav" aria-label="Navigation principale">
            <button
              class="nav-toggle"
              type="button"
              aria-expanded={drawer.open() ? 'true' : 'false'}
              aria-controls="nav-links"
              onClick={drawer.toggle}
              ref={toggleEl}
            >
              <span class="sr-only">{drawer.open() ? 'Fermer le menu' : 'Ouvrir le menu'}</span>
              <svg class="ico ico-menu" aria-hidden="true">
                <use href="#i-menu" />
              </svg>
              <svg class="ico ico-close" aria-hidden="true">
                <use href="#i-close" />
              </svg>
            </button>
            <a class="brand" href="/">
              <Logo />
            </a>
            <ul class={drawer.open() ? 'nav-links is-open' : 'nav-links'} id="nav-links">
              <For each={NAV}>
                {([href, label, anchor]) => (
                  <li>
                    {/* Écouteur direct : le routeur intercepte le clic au niveau du
                        document avant les gestionnaires délégués (onClick) de Solid. */}
                    <a
                      href={href}
                      class={anchor ? 'is-anchor' : undefined}
                      ref={(el) => el.addEventListener('click', () => drawer.close())}
                    >
                      {label}
                    </a>
                  </li>
                )}
              </For>
            </ul>
            <div class="nav-actions">
              <a
                class="btn btn-glass btn-sm nav-cta"
                href="/contact#devenir-partenaire"
                aria-label="Devenir partenaire"
              >
                <span class="cta-full">Devenir partenaire</span>
                <span class="cta-short" aria-hidden="true">
                  Partenaire
                </span>
              </a>
            </div>
          </nav>
        </div>
      </header>

      <main id="main">{props.children}</main>

      <footer class="site-footer">
        <div class="container">
          <div class="footer-grid">
            <div>
              <a class="brand" href="/">
                <Logo lazy />
              </a>
              <p class="footer-tagline">Distribution et logistique alimentaire à travers le Québec.</p>
            </div>
            <div>
              <h2>Services</h2>
              <ul>
                <For each={SERVICES}>
                  {(s) => (
                    <li>
                      <a href="/#services">{s}</a>
                    </li>
                  )}
                </For>
              </ul>
            </div>
            <div>
              <h2>Entreprise</h2>
              <ul>
                <li>
                  <a href="/">Accueil</a>
                </li>
                <li>
                  <a href="/#pourquoi">Pourquoi TG3R</a>
                </li>
                <li>
                  <a href="/contact">Contact</a>
                </li>
                <li>
                  <a href="/contact#devenir-partenaire">Devenir partenaire</a>
                </li>
              </ul>
            </div>
            <div>
              <h2>Contact</h2>
              <address>
                <span class="footer-addr">
                  5225, rue St-Joseph
                  <br />
                  Trois-Rivières (Québec)&nbsp; G8Z 4M5
                </span>
                <a href="tel:+18193865603">819 386-5603</a>
                <a href="mailto:info@tg3r.com">info@tg3r.com</a>
              </address>
            </div>
          </div>
          <div class="footer-bottom">
            <span>© 2026 TG3R. Tous droits réservés.</span>
            <a href="/confidentialite">Politique de confidentialité (Loi 25)</a>
          </div>
        </div>
      </footer>
    </>
  );
}
